import { useState, type FormEvent } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { isAuthenticated, login } from '$lib/lib/auth';
import { Button } from '$lib/components/ui/Button';
import { Input } from '$lib/components/ui/Input';

// Simple PoC auth — single shared password. Mechanism to be replaced before
// any production access. Bypassable in E2E via ?token= query string.
export function AuthGate({ children }: { children: React.ReactNode }) {
  const [authed, setAuthed] = useState<boolean>(() => isAuthenticated());
  if (authed) return <>{children}</>;
  return <LoginScreen onSuccess={() => setAuthed(true)} />;
}

export function LoginScreen({ onSuccess }: { onSuccess: () => void }) {
  const [token, setToken] = useState('');
  const [error, setError] = useState<string | null>(null);
  const location = useLocation();
  const { t } = useTranslation();

  if (isAuthenticated()) {
    return <Navigate to={location.pathname || '/units'} replace />;
  }

  function submit(e: FormEvent) {
    e.preventDefault();
    if (login(token)) {
      setError(null);
      onSuccess();
    } else {
      setError(t('auth.invalid'));
    }
  }

  return (
    <div className="grid min-h-full place-items-center bg-bg p-8">
      <form
        onSubmit={submit}
        data-testid="login-form"
        className="w-full max-w-sm rounded border border-rule bg-surface p-6 shadow-elev-2"
      >
        <h1 className="text-lg font-semibold text-fg">{t('auth.title')}</h1>
        <p className="mt-1 text-xs text-fg-muted">
          {t('auth.subtitle')}
        </p>
        <label htmlFor="poc-token" className="mt-5 block text-xs text-fg-muted">
          {t('auth.tokenLabel')}
        </label>
        <Input
          id="poc-token"
          type="password"
          autoFocus
          value={token}
          onChange={(e) => setToken(e.target.value)}
          className="mt-1"
        />
        {error && (
          <p role="alert" className="mt-2 text-xs text-danger">
            {error}
          </p>
        )}
        <Button type="submit" variant="primary" className="mt-4 w-full">
          {t('auth.continue')}
        </Button>
      </form>
    </div>
  );
}
